import React, { useState } from 'react';
import { TrendingUp, DollarSign, Users, Target, Award, ArrowUp, ArrowRight, CheckCircle, Activity, FileText, CreditCard, ChevronRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, FunnelChart, Funnel, LabelList } from 'recharts';

type ViewType = 'executive' | 'journey' | 'financials';

const FinancialInsightsDashboard = () => {
  const [activeView, setActiveView] = useState<ViewType>('executive');

  // Monthly P&L Data
  const monthlyPL = [
    { month: 'Dec', spend: 6029, revenue: 3604, residuals: 2038, gateway: 756, priority: 810, net: -2425 },
    { month: 'Jan', spend: 11223, revenue: 3458, residuals: 2314, gateway: 580, priority: 565, net: -7765 },
    { month: 'Feb', spend: 8961, revenue: 11365, residuals: 6479, gateway: 4169, priority: 718, net: 2404 },
    { month: 'Mar', spend: 12731, revenue: 28125, residuals: 14734, gateway: 7308, priority: 6083, net: 15395 },
    { month: 'Apr', spend: 14868, revenue: 34249, residuals: 19646, gateway: 7426, priority: 7178, net: 19381 }
  ];

  // Lead Funnel Data
  const leadFunnel = [
    { stage: 'Total Leads', value: 413, fill: '#6366F1' },
    { stage: 'Reached', value: 294, fill: '#8B5CF6' },
    { stage: 'Qualified', value: 166, fill: '#A855F7' },
    { stage: 'Applied', value: 89, fill: '#C084FC' },
    { stage: 'Approved', value: 58, fill: '#E9D5FF' }
  ];

  // Lead Quality Breakdown
  const leadQuality = [
    { category: 'Approved Deals', value: 58, percentage: 14.0, color: '#10B981' },
    { category: 'In Progress', value: 24, percentage: 5.8, color: '#3B82F6' },
    { category: 'Lost After Qualifying', value: 84, percentage: 20.3, color: '#F59E0B' },
    { category: 'Bad Quality', value: 126, percentage: 30.5, color: '#EF4444' },
    { category: 'Never Reached', value: 119, percentage: 28.8, color: '#6B7280' }
  ];

  // Revenue by source
  const revenueByType = [
    { name: 'Vendara Residuals', value: 19646, percentage: 57.4 },
    { name: 'Gateway Revenue', value: 7426, percentage: 21.7 },
    { name: 'Priority Residuals', value: 7178, percentage: 21.0 }
  ];

  const views = {
    executive: {
      title: 'Executive Summary',
      icon: FileText,
      content: (
        <div className="space-y-4 sm:space-y-6">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600">Attributed Investment</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">$39,699</p>
                  <p className="text-xs text-gray-500 mt-1">Targeted + Brand only</p>
                </div>
                <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 ml-2" />
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600">Total Revenue</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">$64,997</p>
                  <p className="text-xs text-green-600 mt-1">↑ 1.64x ROAS</p>
                </div>
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 ml-2" />
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600">Approved Deals</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">58 (14%)</p>
                  <p className="text-xs text-gray-500 mt-1">$1,121 avg LTV (thru Apr 25)</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 ml-2" />
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600">ROI</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">63.7%</p>
                  <p className="text-xs text-gray-500 mt-1">3-month payback</p>
                </div>
                <Award className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 ml-2" />
              </div>
            </div>
          </div>

          {/* Financial Performance Chart */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 space-y-2 sm:space-y-0">
              <h3 className="text-base sm:text-lg font-semibold">5-Month Financial Performance</h3>
              <div className="text-xs text-gray-500 sm:text-right">
                <p>Total P&L Spend: $53,811 (all campaigns)</p>
                <p>Attributed Spend: $39,699 (Targeted + Brand only)</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={monthlyPL}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Area type="monotone" dataKey="revenue" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.8} name="Revenue" />
                <Area type="monotone" dataKey="spend" stackId="2" stroke="#EF4444" fill="#EF4444" fillOpacity={0.8} name="Ad Spend" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Unit Economics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Google Lead Unit Economics</h3>
              <div className="space-y-3">
                <div className="pb-3 border-b">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm text-gray-600">Total Google Leads</span>
                    <span className="font-bold text-sm sm:text-base">413</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-gray-600">Cost per Lead</span>
                    <span className="font-bold text-sm sm:text-base">$96</span>
                  </div>
                </div>
                <div className="pb-3 border-b">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm text-gray-600">Qualified Leads</span>
                    <span className="font-bold text-sm sm:text-base">166 (40.2%)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-gray-600">Cost per Qualified Lead</span>
                    <span className="font-bold text-sm sm:text-base">$239</span>
                  </div>
                </div>
                <div className="pb-3 border-b">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm text-gray-600">Approved Deals</span>
                    <span className="font-bold text-green-600 text-sm sm:text-base">58 (14%)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-gray-600">Cost per Approved Deal</span>
                    <span className="font-bold text-green-600 text-sm sm:text-base">$684</span>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Revenue per Deal (through Apr 25)</span>
                    <span className="font-bold text-blue-600 text-sm sm:text-base">$1,121</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-gray-600">Net Profit per Deal</span>
                    <span className="font-bold text-green-600 text-sm sm:text-base">$437</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Google Lead Type Comparison</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-700">Inbound Calls</h4>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Performer</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Total Leads</p>
                      <p className="text-sm sm:text-lg font-bold">39</p>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Approved Deals</p>
                      <p className="text-sm sm:text-lg font-bold text-green-600">39</p>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Close Rate</p>
                      <p className="text-sm sm:text-lg font-bold text-green-600">9.4%</p>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Avg Revenue</p>
                      <p className="text-sm sm:text-lg font-bold">$928</p>
                    </div>
                  </div>
                  {/* Footnote for Inbound Calls */}
                  <div className="mt-3 bg-blue-50 p-2 rounded text-xs text-gray-600">
                    <span className="font-semibold">Note:</span> 100% of inbound calls were successfully closed (39 out of 39 calls)
                  </div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-700">Form Fills</h4>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">High Volume</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Total Leads</p>
                      <p className="text-sm sm:text-lg font-bold">374</p>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Approved Deals</p>
                      <p className="text-sm sm:text-lg font-bold text-blue-600">19</p>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Close Rate</p>
                      <p className="text-sm sm:text-lg font-bold text-blue-600">4.6%</p>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded">
                      <p className="text-xs text-gray-600">Avg Revenue</p>
                      <p className="text-sm sm:text-lg font-bold">$1,580</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    journey: {
      title: 'Lead Journey Analytics',
      icon: Activity,
      content: (
        <div className="space-y-4 sm:space-y-6">
          {/* Lead Funnel Visualization */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Lead Conversion Funnel</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
              {leadFunnel.map((stage, index) => (
                <div key={index} className="relative">
                  <div className={`text-center p-3 sm:p-4 rounded-lg bg-gradient-to-b ${
                    index === 0 ? 'from-indigo-500 to-indigo-600' :
                    index === 1 ? 'from-purple-500 to-purple-600' :
                    index === 2 ? 'from-purple-600 to-purple-700' :
                    index === 3 ? 'from-purple-700 to-purple-800' :
                    'from-purple-800 to-purple-900'
                  } text-white`}>
                    <p className="text-lg sm:text-2xl font-bold">{stage.value}</p>
                    <p className="text-xs mt-1">{stage.stage}</p>
                  </div>
                  {index < leadFunnel.length - 1 && (
                    <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                  )}
                  {index > 0 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {((stage.value / leadFunnel[index - 1].value) * 100).toFixed(1)}% converted
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Lead Quality Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Lead Quality Breakdown</h3>
              <div className="space-y-3">
                {leadQuality.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs sm:text-sm text-gray-600">{item.category}</span>
                      <span className="text-xs sm:text-sm font-bold">{item.value} ({item.percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${item.percentage}%`,
                          backgroundColor: item.color
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Key Conversion Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Reach Rate (of total)</span>
                  <span className="font-bold text-sm sm:text-base">71.2%</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Qualification Rate (of total)</span>
                  <span className="font-bold text-sm sm:text-base">40.2%</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Application Rate (of qualified)</span>
                  <span className="font-bold text-sm sm:text-base">53.6%</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Bank Approval Rate (of applied)</span>
                  <span className="font-bold text-sm sm:text-base">65.2%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Google Success Metrics */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Google Campaign Success Indicators</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl sm:text-3xl font-bold text-green-600">14%</p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Lead to Deal Conversion</p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-2xl sm:text-3xl font-bold text-orange-600">19</p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Bank Declines</p>
                <p className="text-xs text-gray-500 mt-2">Increased in April</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl sm:text-3xl font-bold text-purple-600">$437</p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Profit per Deal (thru Apr 25)</p>
                <p className="text-xs text-gray-500 mt-2">63.7% margin</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-700">
                <span className="font-semibold">Key Challenge:</span> Bank underwriting became more restrictive in April, declining 19 qualified applications. 
                With historical approval rates, revenue would have been ~$12,000 higher.
              </p>
            </div>
          </div>
        </div>
      )
    },
    financials: {
      title: 'Campaign P&L Analysis',
      icon: CreditCard,
      content: (
        <div className="space-y-4 sm:space-y-6">
          {/* Monthly P&L Table */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Monthly P&L Breakdown</h3>
            <div className="mb-3 p-3 bg-gray-50 rounded text-xs sm:text-sm">
              <p className="text-gray-700 mb-1">
                <span className="font-semibold">Ad Spend:</span> Reflects actual Google Ads spend from campaign reports (only expense)
              </p>
              <p className="text-gray-700 mb-1">
                <span className="font-semibold">Includes:</span> All Google campaigns (Targeted, Brand, Healthcare, Display, Generic)
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Revenue Note:</span> Includes one additional non-Google channel in total revenue figures
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Month</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Ad Spend</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Gateway Rev</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Vendara</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Priority</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Total Rev</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">Net P&L</th>
                    <th className="text-right py-2 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-gray-600">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyPL.map((month, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm">{month.month} 2024</td>
                      <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right text-red-600">-${month.spend.toLocaleString()}</td>
                      <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">${month.gateway.toLocaleString()}</td>
                      <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">${month.residuals.toLocaleString()}</td>
                      <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">${month.priority.toLocaleString()}</td>
                      <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right font-semibold">${month.revenue.toLocaleString()}</td>
                      <td className={`py-2 px-2 sm:px-4 text-xs sm:text-sm text-right font-bold ${month.net > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${month.net.toLocaleString()}
                      </td>
                      <td className={`py-2 px-2 sm:px-4 text-xs sm:text-sm text-right font-bold ${month.net > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {((month.revenue - month.spend) / month.spend * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-gray-100">
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm">Total</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right text-red-600">-$53,811</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">$20,238</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">$45,211</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">$15,353</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right">$80,802</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right text-green-600">$26,991</td>
                    <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-right text-green-600">50.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Revenue Composition */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Revenue Mix (April 2025)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={revenueByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="#3B82F6" />
                    <Cell fill="#10B981" />
                    <Cell fill="#8B5CF6" />
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {revenueByType.map((item, index) => (
                  <div key={index} className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">{item.name}</span>
                    <span className="font-semibold">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Growth Metrics</h3>
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-gray-600">Monthly Revenue Growth</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">212.6%</p>
                  <p className="text-xs text-gray-500 mt-1">Average monthly increase</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-gray-600">Breakeven Achieved</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">March 2025</p>
                  <p className="text-xs text-gray-500 mt-1">Month 3 of campaign</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-gray-600">Current Monthly Profit</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">$19,381</p>
                  <p className="text-xs text-gray-500 mt-1">April 2025</p>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Performance */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Campaign Breakdown</h3>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded space-y-2 sm:space-y-0">
                <div>
                  <p className="font-semibold text-sm sm:text-base">Targeted Search</p>
                  <p className="text-xs sm:text-sm text-gray-600">69% of budget</p>
                </div>
                <div className="sm:text-right">
                  <p className="font-bold text-sm sm:text-base">$36,684</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded space-y-2 sm:space-y-0">
                <div>
                  <p className="font-semibold text-sm sm:text-base">Brand Search</p>
                  <p className="text-xs sm:text-sm text-gray-600">6.5% of budget</p>
                </div>
                <div className="sm:text-right">
                  <p className="font-bold text-sm sm:text-base">$2,452</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">247Payments - Google Channel Performance</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Financial insights and performance metrics for paid media investment</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-col sm:flex-row sm:space-x-4 mb-6 sm:mb-8 border-b overflow-x-auto">
          {Object.entries(views).map(([key, view]) => (
            <button
              key={key}
              onClick={() => setActiveView(key as ViewType)}
              className={`flex items-center px-3 sm:px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeView === key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <view.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              <span className="text-sm sm:text-base">{view.title}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
          {views[activeView].content}
        </div>

        {/* Footer */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-xs sm:text-sm text-gray-500">247Payments Campaign P/L</p>
        </div>
      </div>
    </div>
  );
};

export default FinancialInsightsDashboard;